import { useState } from 'react';
import { Building2, Phone, User } from 'lucide-react';
import { CrmContactBar } from './CrmContactBar';
import { CrmDealAccordion } from './CrmDealAccordion';
import { CrmClientContactEditor, type CrmClientContactValues } from './CrmClientContactEditor';
import type { CrmParsedContact } from '../../../lib/crmContact';

type Props = {
  contact: CrmParsedContact & { displayName: string };
  deliveryAddress?: string | null;
  userId?: number;
  orderId?: number | null;
  syncOrderId?: number | null;
  canEdit?: boolean;
  telegramId?: number | null;
  username?: string | null;
  hapticSelection: () => void;
  hapticSuccess?: () => void;
  onContactSaved?: () => void;
  defaultOpen?: boolean;
};

export function CrmClientContactBlock({
  contact,
  deliveryAddress,
  userId,
  orderId,
  syncOrderId,
  canEdit = false,
  telegramId,
  username,
  hapticSelection,
  hapticSuccess,
  onContactSaved,
  defaultOpen = false,
}: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [values, setValues] = useState<CrmClientContactValues>({
    name: contact.name,
    phone: contact.phone,
    company: contact.company,
    delivery_address: deliveryAddress ?? null,
  });

  const hasPhone = Boolean(values.phone?.trim());
  const hasCompany = Boolean(values.company?.trim());
  const editable = canEdit && Boolean(userId);

  return (
    <CrmDealAccordion
      macroId="sales"
      title="Контакты клиента"
      subtitle={[values.name || contact.displayName, values.phone, values.delivery_address]
        .filter(Boolean)
        .join(' · ')
        .slice(0, 72)}
      accent="#68D391"
      icon={<User size={15} color="#68D391" />}
      open={open}
      onOpenChange={setOpen}
      defaultOpen={defaultOpen}
    >
      <div className="space-y-2">
        {editable ? (
          <CrmClientContactEditor
            initial={values}
            userId={userId!}
            orderId={orderId}
            syncOrderId={syncOrderId}
            canEdit
            hapticSelection={hapticSelection}
            hapticSuccess={hapticSuccess}
            onSaved={(next) => {
              setValues(next);
              onContactSaved?.();
            }}
          />
        ) : (
          <div
            className="rounded-xl p-3 space-y-2"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}
          >
            <p className="text-xs font-bold flex items-center gap-2" style={{ color: 'var(--text-1)' }}>
              <User size={14} className="shrink-0" style={{ color: '#68D391' }} />
              {contact.displayName}
            </p>
            {hasPhone && (
              <p className="text-xs flex items-center gap-2" style={{ color: 'var(--text-2)' }}>
                <Phone size={14} className="shrink-0" style={{ color: 'var(--text-3)' }} />
                {values.phone}
              </p>
            )}
            {hasCompany && (
              <p className="text-xs flex items-center gap-2" style={{ color: 'var(--text-2)' }}>
                <Building2 size={14} className="shrink-0" style={{ color: 'var(--text-3)' }} />
                {values.company}
              </p>
            )}
            {values.delivery_address && (
              <p className="text-xs" style={{ color: 'var(--text-2)' }}>
                {values.delivery_address}
              </p>
            )}
            {!hasPhone && !hasCompany && !values.delivery_address && (
              <p className="text-[10px]" style={{ color: 'var(--text-3)' }}>
                Телефон и адрес не указаны — откройте карточку с правами менеджера или заполните при оформлении.
              </p>
            )}
          </div>
        )}
        {(hasPhone || telegramId) && (
          <CrmContactBar
            phone={values.phone}
            telegramId={telegramId}
            username={username}
            hapticSelection={hapticSelection}
          />
        )}
      </div>
    </CrmDealAccordion>
  );
}
